import React, { useEffect, useRef, useState } from 'react'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { useIntl } from 'react-intl'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { CircularProgress, LinearProgress, MenuItem, Select } from '@mui/material'
import styled from '@emotion/styled'
import { H3 } from '../../../layouts/TextStyles'
import { capitalize } from '../../../utils/functions'
import { ui } from '../../../utils/uiTokens'
import { sectionApiSlice } from '../../../features/section/sectionSlice'
import GroupStudentsTable from './components/GroupStudentsTable'
import MonthWeekFilter from '../../../components/monthWeekFilter/MonthWeekFilter'
import Breadcrumb from '../../../components/Breadcrumb'
import { DEFAULT_PAGE_SIZE } from '../../../utils/constants'
import { useInfiniteScroll } from '../../../app/hooks/useInfiniteScroll'
import { StudentSectionAnalysisState } from '../../../features/types'

const GroupStudentsPage = () => {
    const { formatMessage } = useIntl()
    const { state } = useLocation()
    const { groupId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams('')
    const [monthNumber, setMonthNumber] = usePersistedState<number | undefined>(
        'curator.monthNumber',
        Number(searchParams.get('monthNumber')) || undefined,
    )
    const [weekNumber, setWeekNumber] = usePersistedState<number | undefined>(
        'curator.weekNumber',
        Number(searchParams.get('weekNumber')) || undefined,
    )
    // Сортировка по проценту столбца: 0 нет, 1 видео, 2 тест; направление 0/1 убыв/2 возр
    const [sortColumn, setSortColumn] = useState(0)
    const [sortDir, setSortDir] = useState(0)
    // Фильтр диапазона процента: 0 все, 1 = 0-50%, 2 = больше 50%
    const [videoFilter, setVideoFilter] = useState(0)
    const [testFilter, setTestFilter] = useState(0)
    const [rows, setRows] = useState<StudentSectionAnalysisState[]>([])
    const [totalCount, setTotalCount] = useState(0)
    const [loadedPages, setLoadedPages] = useState(0)
    // Ответы приходят не по порядку: применяем только последний запрос
    const requestIdRef = useRef(0)
    const isLoadingRef = useRef(false)
    // Наблюдатель может сработать до того, как новая порция легла в state,
    // и снова попросить ту же страницу: она бы добавилась в список второй раз
    const loadedPagesRef = useRef(0)

    const [fetchStudents, { isFetching }] =
        sectionApiSlice.useLazyGetStudentsSectionAnalysisByGroupQuery()

    const hasMore = rows.length < totalCount

    const loadPage = async (nextPage: number) => {
        if (nextPage > 1 && (isLoadingRef.current || nextPage <= loadedPagesRef.current)) return

        const requestId = ++requestIdRef.current
        isLoadingRef.current = true

        try {
            const response = await fetchStudents({
                groupId: +groupId,
                page: nextPage - 1,
                size: DEFAULT_PAGE_SIZE,
                monthNumber,
                weekNumber,
                sortColumn,
                sortDir,
                videoFilter,
                testFilter,
            }).unwrap()

            if (requestId !== requestIdRef.current) return

            setRows((prev) => (nextPage === 1 ? response.data : [...prev, ...response.data]))
            // Короткая страница означает конец списка: расхождение с totalCount
            // иначе гоняло бы наблюдатель по пустым страницам без остановки
            setTotalCount(response.data.length < DEFAULT_PAGE_SIZE
                ? (nextPage - 1) * DEFAULT_PAGE_SIZE + response.data.length
                : response.totalCount)
            setLoadedPages(nextPage)
            loadedPagesRef.current = nextPage
        } catch (error) {
            // Без остановки наблюдатель повторял бы упавший запрос бесконечно
            if (requestId === requestIdRef.current) setTotalCount((nextPage - 1) * DEFAULT_PAGE_SIZE)
        } finally {
            if (requestId === requestIdRef.current) isLoadingRef.current = false
        }
    }

    const sentinelRef = useInfiniteScroll(
        () => loadPage(loadedPages + 1),
        hasMore && !isFetching && loadedPages > 0,
    )

    // Проценты считаются по темам выбранного периода, поэтому список
    // после смены месяца перечитывается с первой страницы
    const handlePeriodChange = (month?: number, week?: number) => {
        setMonthNumber(month)
        setWeekNumber(week)
    }

    // Клик по заголовку: первый раз по убыванию, второй по возрастанию
    const handleSort = (column: number) => {
        if (sortColumn !== column) {
            setSortColumn(column)
            setSortDir(1)
        } else {
            setSortDir((prev) => (prev === 1 ? 2 : 1))
        }
    }

    useEffect(() => {
        const params: Record<string, string> = {}
        if (monthNumber) params.monthNumber = monthNumber.toString()
        if (weekNumber) params.weekNumber = weekNumber.toString()
        // replace, иначе каждый выбор месяца кладёт запись в историю и стрелка
        // "назад" возвращает на эту же страницу, а не к списку групп
        setSearchParams(params, { state, replace: true })

        setRows([])
        setTotalCount(0)
        setLoadedPages(0)
        loadedPagesRef.current = 0
        loadPage(1)
    }, [groupId, monthNumber, weekNumber, sortColumn, sortDir, videoFilter, testFilter])

    return (
        <Wrapper>
            <Breadcrumb
                links={[
                    formatMessage({ id: 'groups' }),
                    formatMessage({ id: 'studentsList' }),
                ]}
            />

            <Content>
                <Header>
                    <Title>
                        <H3>
                            {formatMessage({ id: 'students' })}{' '}
                            {state?.group?.title}
                        </H3>
                    </Title>

                    <FilterGroup>
                        <MonthWeekFilter
                            groupId={groupId ? +groupId : undefined}
                            monthNumber={monthNumber}
                            weekNumber={weekNumber}
                            onChange={handlePeriodChange}
                        />

                        <Select
                            size="small"
                            value={videoFilter}
                            onChange={(e) => setVideoFilter(Number(e.target.value))}
                            sx={percentSelect}
                        >
                            <MenuItem value={0}>{capitalize(formatMessage({ id: 'videoLesson' }))}: {formatMessage({ id: 'all' })}</MenuItem>
                            <MenuItem value={1}>{capitalize(formatMessage({ id: 'videoLesson' }))}: {formatMessage({ id: 'percent0to50' })}</MenuItem>
                            <MenuItem value={2}>{capitalize(formatMessage({ id: 'videoLesson' }))}: {formatMessage({ id: 'percentOver50' })}</MenuItem>
                        </Select>

                        <Select
                            size="small"
                            value={testFilter}
                            onChange={(e) => setTestFilter(Number(e.target.value))}
                            sx={percentSelect}
                        >
                            <MenuItem value={0}>{formatMessage({ id: 'test' })}: {formatMessage({ id: 'all' })}</MenuItem>
                            <MenuItem value={1}>{formatMessage({ id: 'test' })}: {formatMessage({ id: 'percent0to50' })}</MenuItem>
                            <MenuItem value={2}>{formatMessage({ id: 'test' })}: {formatMessage({ id: 'percentOver50' })}</MenuItem>
                        </Select>
                    </FilterGroup>
                </Header>

                {isFetching && loadedPages === 0 ? (
                    <LinearProgress />
                ) : (
                    <>
                        <GroupStudentsTable
                            students={rows}
                            isFetching={isFetching}
                            sortColumn={sortColumn}
                            sortDir={sortDir}
                            onSort={handleSort}
                        />

                        <Sentinel ref={sentinelRef}>
                            {isFetching && <CircularProgress size={22} />}
                        </Sentinel>
                    </>
                )}
            </Content>
        </Wrapper>
    )
}

export default GroupStudentsPage

const Wrapper = styled.div`
    margin: 10px;
    border-radius: 16px;
`
const Sentinel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 48px;
`

const Title = styled.div`
    margin-bottom: 20px;
    text-align: left;
    display: flex;
    align-items: center;
    color: ${ui.ink};
`

const Content = styled.div`
    background-color: ${ui.card};
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 24px;
    min-height: 80vh;
`

const Header = styled.header`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 16px;
    align-items: center;
    justify-content: space-between;
`

const FilterGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
`

const percentSelect = {
    minWidth: 190,
    borderRadius: ui.radius,
    fontSize: '13.5px',
    fontWeight: 600,
    color: ui.ink,
    backgroundColor: ui.card,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ui.border },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.pink },
}
